import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useGetResourceEventsQuery, useGetTrendsQuery } from '../healthWalletSlice';
import type { ChartData } from '../healthWalletSlice';
import { formatDate } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import { EventTimeline } from '../EventTimeline';
import { ValueChart } from '../ValueChart';
import { DocHistoryError } from './DocHistoryError';
import { obsLatestLabel, trendBadge } from './classify';
import { V1_ACCENT } from './theme';

// ============================================================================
// Observations detail dialog (RendererV1) — shared frame + fact row; CHARTABLE
// groups reuse ValueChart from the Trends cache (which also carries the
// Settings-gated AI summary for free); everything gets the shared EventTimeline
// as documentation history.
// ============================================================================

export const ObservationDetailDialog: FC<{
  group: GroupedSummary | null;
  open: boolean;
  onClose: () => void;
}> = ({ group, open, onClose }) => {
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  // Option 2 drill-down: send the card's KEY (code-or-name identity), not the display name — the backend obs
  // events fn scopes by key. `group.key` is undefined for the nameless "other" bucket → '' (nameless).
  const { data: events, isLoading, isFetching, isError, refetch } = useGetResourceEventsQuery(
    { resource: 'observations', name: group?.key ?? '' },
    { skip: !group || !open }
  );
  const { data: trends } = useGetTrendsQuery();
  // Match the trend series by the analyte KEY (both card + trend carry it), NOT the display name — so a
  // code-keyed card whose display name is a merged spelling still finds its trend.
  const series = useMemo(() => (trends ?? []).find((x) => x.resource === 'observations' && x.key === group?.key), [trends, group]);
  const chart: ChartData | null = useMemo(() => {
    if (!series || series.data.length < 2) return null;
    return { unit: series.unit ?? '', data: series.data.map((p, i) => ({ date: p.date ?? '', value: p.value, eventIndex: i })) };
  }, [series]);
  if (!group) return null;
  const loading = isLoading || isFetching;
  const latest = obsLatestLabel(group);
  // Range status (Below/In/Above range) for the header — same trendBadge as the card + the Trends detail dialog.
  const badge = series && series.data.length > 1 ? trendBadge(series) : null;
  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 120, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{group.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
            {badge && (
              <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: badge.bg, color: badge.fg }}>{badge.label}</Box>
            )}
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>as documented in your health record</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        {/* Fact row */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {fact('Latest', latest ?? '—')}
          {/* Same calendar day for first & last → show one "Date recorded" (formatDate is day-level). */}
          {group.firstDate && formatDate(group.firstDate) === formatDate(group.lastDate) ? (
            fact('Date recorded', formatDate(group.firstDate), true)
          ) : (
            <>
              {fact('First documented', group.firstDate ? formatDate(group.firstDate) : '—', true)}
              {fact('Most recent', group.lastDate ? formatDate(group.lastDate) : '—', true)}
            </>
          )}
          {fact('Readings', group.count.toLocaleString('en-US'), true)}
        </Paper>

        {/* Chart (chartable groups; carries the Settings-gated AI summary) */}
        {chart && (
          <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white' }}>
            <ValueChart chart={chart} resource="observations" groupName={group.name} groupKey={group.key ?? ''} selectedEvent={selectedEvent} onClick={setSelectedEvent} />
          </Paper>
        )}

        {/* Documentation history */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px 8px', bgcolor: 'white' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase', mb: 2 }}>Documentation history</Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Box>
          ) : isError ? (
            <DocHistoryError onRetry={refetch} />
          ) : (
            <EventTimeline eventList={events ?? []} chartData={null} resource="observations" groupName={group.name} growWithContent />
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
